import * as Sentry from "@sentry/react";
import { ScreenSize, UI } from "@wwimmo/ui";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Route, selectRoute } from "src/config/routes";
import { RootStoreContext } from "src/stores/RootStore";
import { TICKET_STATE, Ticket } from "src/stores/TicketStore";
import { SCREEN_SIZE } from "src/stores/UIStore";
import {
    FilterStateType,
    SORT_FIELD,
    SearchTag,
    TicketListStore,
    hasNewMessagesInState
} from "src/stores/tickets/TicketListStore";
import { SortableHeaderCell } from "./SortableHeader";
import { TicketListRowDesktop } from "./TicketListRowDesktop";
import { TicketListRowMobile } from "./TicketListRowMobile";
import styles from "./TicketsList.module.css";

interface TicketListProps {
    onFilterChange: (id: FilterStateType) => void;
}

const TicketsListBase = (props: TicketListProps) => {
    const { onFilterChange } = props;
    const { t } = useTranslation();
    const { uiStore, ticketListStore, authStore, ticketStore } = useContext(RootStoreContext);

    useEffect(() => {
        ticketStore.currentTicket?.reset();
        // eslint-disable-next-line
    }, []);

    // Helper function to update tag with checks
    const updateTag = (id: string, name: string, label: string, ticketListStore: TicketListStore) => {
        const tagExists = ticketListStore.searchTags.some((tag: SearchTag) => tag.id === id);

        if (!tagExists) {
            ticketListStore.addSearchTag(label, id, true);
        } else {
            const tagLabel = ticketListStore.searchTags.find((tag: SearchTag) => tag.id === id)?.label;
            if (tagLabel !== label) {
                ticketListStore.searchTags = ticketListStore.searchTags.map((tag: SearchTag) =>
                    tag.id === id ? { ...tag, active: true, label } : tag
                );
            }
        }
        ticketListStore.setTag(id, true);
    };

    // Parse the query parameters from the URL
    const parseQueryParams = () => {
        const params = new URLSearchParams(window.location.search);
        let realestateId = params.get("realestateId");
        let unitId = null;

        // Handle malformed query string
        if (realestateId && realestateId.includes("?unitId=")) {
            [realestateId, unitId] = realestateId.split("?unitId=");
        } else {
            unitId = params.get("unitId");
        }

        return { realestateId, unitId };
    };

    // Handles both realestateId and unitId setting logic
    const handleId = async (
        id: string | null,
        idType: "realestateId" | "unitId",
        tagId: string,
        ticketListStore: TicketListStore,
        getName: () => Promise<string | null>
    ) => {
        if (!id || id.length === 0) return;

        const name = (await getName()) || "";

        runInAction(() => {
            updateTag(tagId, id, name, ticketListStore);
            ticketListStore.setTag("SHOW_OWN", false);
        });
    };

    // Handles realestateId and unitId together
    const handleTags = async (realestateId: string | null, unitId: string | null, ticketListStore: TicketListStore) => {
        await handleId(
            realestateId,
            "realestateId",
            realestateId ? `SHOW_REALESTATE:${realestateId}` : "SHOW_REALESTATE",
            ticketListStore,
            ticketListStore.getRealestateName
        );
        await handleId(
            unitId,
            "unitId",
            unitId ? `SHOW_UNIT:${unitId}` : "SHOW_UNIT",
            ticketListStore,
            ticketListStore.getUnitName
        );
    };

    // Helper function to update ID if it's not already set or is different
    const updateIdIfNeeded = (currentId: string | null, newId: string | null, setId: (id: string) => void) => {
        if (!currentId || currentId.length === 0 || (newId && currentId !== newId)) {
            setId(newId || "");
        }
    };

    useEffect(() => {
        const { realestateId, unitId } = parseQueryParams();

        const handleRealestateTags = async () => {
            if (realestateId !== null && realestateId !== "" && realestateId !== ticketListStore.realestateId) {
                ticketListStore.setUnitId("");
                ticketListStore.setTag("SHOW_UNIT", false);
            }

            // Update realestateId and unitId only if necessary
            updateIdIfNeeded(ticketListStore.realestateId, realestateId, ticketListStore.setRealestateId);
            updateIdIfNeeded(ticketListStore.unitId, unitId, ticketListStore.setUnitId);

            await handleTags(realestateId, unitId, ticketListStore);
        };

        handleRealestateTags();
        // eslint-disable-next-line
    }, [ticketListStore]);

    const getColumnWidths = useCallback(() => {
        const columnWidthsDesktop = [0, 0.04, 0.04, 0.1, 0.18, 0.15, 0.18, 0.22, 0.09, 0.125];
        const columnWidthsLaptopL = [0, 0, 0.06, 0.1, 0.18, 0.15, 0.18, 0.22, 0.09, 0.125];
        const columnWidthsLaptop = [0, 0, 0.08, 0.1, 0.2, 0.22, 0.26, 0.18, 0.13, 0];
        const columnWidthsTablet = [0, 0, 0.1, 0.1, 0.28, 0.27, 0.26, 0.18, 0.13, 0];

        return uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L
            ? columnWidthsDesktop
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP_L
              ? columnWidthsLaptopL
              : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP
                ? columnWidthsLaptop
                : columnWidthsTablet;
    }, [uiStore.currentScreenSize]);

    const columnWidths = getColumnWidths();

    const renderItem = useCallback(
        (ticket: Ticket, screenSize: ScreenSize) => {
            const columnWidths = getColumnWidths();

            const listRows = [
                screenSize === ScreenSize.MOBILE ? (
                    <TicketListRowMobile ticket={ticket} key={`row-${ticket.number}`} />
                ) : (
                    <TicketListRowDesktop ticket={ticket} key={`row-${ticket.number}`} widths={columnWidths} />
                )
            ];
            return (
                <UI.List.Item
                    key={`item-${ticket.number}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={
                        ticket.hasNewMessage ? `${styles.ListItem} ${styles.ListItemNewMessage}` : styles.ListItem
                    }
                    isClickableItemWithoutChevron
                    to={
                        ticket.number
                            ? selectRoute(Route.ticketDetails, authStore.user?.role, {
                                  ticketnumber: String(ticket.number)
                              })
                            : Route.tickets
                    }
                />
            );
        },
        [authStore.user?.role, getColumnWidths]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }

            const { sortField, sortDirection } = ticketListStore.ticketFilter;

            const rows = [
                <SortableHeaderCell
                    key="newMessage"
                    className={styles.NewMessageHeaderCell}
                    field={SORT_FIELD.NEW_MESSAGE}
                    label={<UI.Icon icon={UI.SVGIcon.Mail} size="small" />}
                    colspan={columnWidths[0]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />,
                <UI.List.Cell
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP_L ? "d-none" : "d-flex"}`}
                    key="type"
                    colspan={columnWidths[1]}
                    value=""
                    truncate
                />,
                <SortableHeaderCell
                    key="nr"
                    field={SORT_FIELD.NUMBER}
                    label={<Trans>screens.tickets.list.nr</Trans>}
                    colspan={columnWidths[2]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />,
                <SortableHeaderCell
                    key="date"
                    field={SORT_FIELD.DATE}
                    label={<Trans>screens.tickets.list.date</Trans>}
                    colspan={columnWidths[3]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />,
                <SortableHeaderCell
                    key="label"
                    field={SORT_FIELD.TITLE}
                    label={<Trans>screens.tickets.list.label</Trans>}
                    colspan={columnWidths[4]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />,
                <SortableHeaderCell
                    key="realestate"
                    field={SORT_FIELD.REALESTATE}
                    label={<Trans>screens.tickets.list.realestate</Trans>}
                    colspan={columnWidths[5]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />,
                <SortableHeaderCell
                    className={`${uiStore.currentScreenSize < SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                    key="incidentpersons"
                    field={SORT_FIELD.INCIDENT_PERSONS}
                    label={<Trans>screens.tickets.list.incidentpersons</Trans>}
                    colspan={columnWidths[6]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />,
                <SortableHeaderCell
                    key="assigned"
                    field={SORT_FIELD.ASSIGNED}
                    label={<Trans>screens.tickets.list.assigned</Trans>}
                    colspan={columnWidths[7]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />,
                <SortableHeaderCell
                    key="orderlist"
                    field={SORT_FIELD.ORDERLIST}
                    label={<Trans>screens.tickets.list.orderlist</Trans>}
                    colspan={columnWidths[8]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />,
                <SortableHeaderCell
                    key="status"
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                    field={SORT_FIELD.STATUS}
                    label={<Trans>screens.tickets.list.status</Trans>}
                    colspan={columnWidths[9]}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                />
            ];
            return <UI.List.Header rows={rows} className="p-0" />;
        },
        [columnWidths, uiStore.currentScreenSize, ticketListStore]
    );

    const stats = ticketListStore.ticketStateCounts;
    const newMessageStats = ticketListStore.newMessageStateCounts;
    const onPageChange = useCallback(
        (page: number) => {
            const offset = (page - 1) * ticketListStore.pageLimit;
            ticketListStore.setPageOffset(offset);
        },
        [ticketListStore]
    );

    const additionalFilterStateItems = useMemo(
        () => [
            {
                label: `${t("easyticket.states.completed")} ${
                    ticketStore.initialLoadAllTicketsCompleted ? `(${stats.completed})` : ""
                }`,
                id: TICKET_STATE.COMPLETED,
                marker: hasNewMessagesInState(newMessageStats, TICKET_STATE.COMPLETED)
            },
            {
                label: `${t("easyticket.states.archived")} ${
                    ticketStore.initialLoadAllTicketsCompleted ? `(${stats.archived})` : ""
                }`,
                id: TICKET_STATE.ARCHIVED,
                marker: hasNewMessagesInState(newMessageStats, TICKET_STATE.ARCHIVED)
            },
            {
                label: `${t("labels.all")} ${
                    ticketStore.initialLoadAllTicketsCompleted
                        ? `(${stats.new + stats.assign_incident + stats.assigned + stats.comissioned + stats.completed + stats.archived})`
                        : ""
                }`,
                id: "All",
                marker: hasNewMessagesInState(newMessageStats, "All")
            }
        ],
        [t, stats, newMessageStats, ticketStore.initialLoadAllTicketsCompleted]
    );

    const filterStateItems = useMemo(
        () => [
            {
                label: `${t("easyticket.states.new")} ${
                    ticketStore.initialLoadAllTicketsCompleted ? `(${stats.new + stats.assign_incident})` : ""
                }`,
                id: TICKET_STATE.NEW,
                marker: hasNewMessagesInState(newMessageStats, TICKET_STATE.NEW)
            },
            {
                label: `${t("easyticket.states.assigned")} ${
                    ticketStore.initialLoadAllTicketsCompleted ? `(${stats.assigned})` : ""
                }`,
                id: TICKET_STATE.ASSIGNED,
                marker: hasNewMessagesInState(newMessageStats, TICKET_STATE.ASSIGNED)
            },
            {
                label: `${t("easyticket.states.comissioned")} ${
                    ticketStore.initialLoadAllTicketsCompleted ? `(${stats.comissioned})` : ""
                }`,
                id: TICKET_STATE.COMISSIONED,
                marker: hasNewMessagesInState(newMessageStats, TICKET_STATE.COMISSIONED)
            },
            {
                label: `${t("easyticket.states.all_open")} ${
                    ticketStore.initialLoadAllTicketsCompleted
                        ? `(${stats.new + stats.assigned + stats.comissioned})`
                        : ""
                }`,
                id: "AllOpen",
                marker: hasNewMessagesInState(newMessageStats, "AllOpen")
            },
            {
                label: `${t("easyticket.states.additional")} ...`,
                id: TICKET_STATE.ADDITIONAL,
                marker: hasNewMessagesInState(newMessageStats, TICKET_STATE.ADDITIONAL)
            }
        ],
        [t, stats, newMessageStats, ticketStore.initialLoadAllTicketsCompleted]
    );

    const [currentFilterStateItems, setCurrentFilterStateItems] = useState(filterStateItems);

    useEffect(() => {
        if (currentFilterStateItems.length === 5) {
            setCurrentFilterStateItems(filterStateItems);
        } else if (currentFilterStateItems.length === 7) {
            const firstFourItems = filterStateItems.slice(0, 4);

            // Update the last 3 items of currentFilterStateItems based on additionalFilterStateItems
            const lastThreeUpdatedItems = currentFilterStateItems.slice(4).map((item) => {
                if (item.id === TICKET_STATE.COMPLETED || item.id === TICKET_STATE.ARCHIVED || item.id === "All") {
                    const replacement = additionalFilterStateItems.find(
                        (additionalItem) => additionalItem.id === item.id
                    );
                    return replacement ? replacement : item;
                }
                return item;
            });
            const updatedFilterStateItems = [...firstFourItems, ...lastThreeUpdatedItems];

            setCurrentFilterStateItems(updatedFilterStateItems);
        }

        // eslint-disable-next-line
    }, [additionalFilterStateItems]);

    const handleAdditionalTicketState = useCallback(() => {
        ticketStore.initialLoadAllTickets(true);

        // Create the new updated filterStateItems without "Additional"
        const updatedFilterStateItems = [
            ...filterStateItems.filter((item) => item.id !== TICKET_STATE.ADDITIONAL),
            ...additionalFilterStateItems
        ];

        setCurrentFilterStateItems(updatedFilterStateItems);

        onFilterChange(TICKET_STATE.COMPLETED as FilterStateType);
    }, [filterStateItems, additionalFilterStateItems, ticketStore, onFilterChange]);

    const onSelectDropdown = useCallback(
        (id: string) => {
            if (id === TICKET_STATE.ADDITIONAL) {
                handleAdditionalTicketState();
                return;
            }

            onFilterChange(id as FilterStateType);
        },
        [onFilterChange, handleAdditionalTicketState]
    );

    const onTabsChange = useCallback(
        (id: string) => {
            ticketListStore.setIsTabAdditionalClicked(
                id === TICKET_STATE.ADDITIONAL ||
                    id === TICKET_STATE.COMPLETED ||
                    id === TICKET_STATE.ARCHIVED ||
                    id === "AllOpen" ||
                    id === "All"
            );

            if (id === TICKET_STATE.ADDITIONAL) {
                handleAdditionalTicketState();
                return;
            }

            // For any other case, proceed with the regular filter change
            onFilterChange(id as FilterStateType);
        },
        [onFilterChange, ticketListStore, handleAdditionalTicketState]
    );

    return (
        <UI.Card flex={true}>
            {uiStore.isMobile ? (
                <UI.Dropdown
                    id="sort"
                    onSelect={onSelectDropdown}
                    items={currentFilterStateItems.map((item) => ({
                        label: item.label,
                        value: item.id,
                        marker: item.marker
                    }))}
                    label={
                        currentFilterStateItems.find((item) => item.id === ticketListStore.ticketFilter.state)?.label ??
                        ""
                    }
                    className="mb-2"
                />
            ) : (
                <div className={styles.Tabbar}>
                    <UI.Tabs
                        selectedId={ticketListStore.ticketFilter.state}
                        onTabChange={onTabsChange}
                        contents={currentFilterStateItems}
                        nowrap
                    ></UI.Tabs>
                </div>
            )}
            <UI.List.BasicList
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={ticketListStore.tickets}
                renderItem={renderItem}
                renderHeader={renderHeader}
                totalCount={ticketListStore.currentTabTicketCount}
                // eslint-disable-next-line
                onPageChanged={onPageChange}
                itemsPerPage={20}
                emptyListMessage={
                    ticketListStore.tickets.length === 0
                        ? t("screens.tickets.list.tickets_are_loading").toString()
                        : t("screens.tickets.list.no_tickets_found").toString()
                }
            />
        </UI.Card>
    );
};

export const TicketsList = Sentry.withProfiler(observer(TicketsListBase));
